import { useState } from 'react';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { APP_NAME } from '../../config/appConstants';
import AuthPageFooter from '../../shared/components/common/AuthPageFooter';
import './Login.css';
import appLogo from '../../assets/images/DevicePulse-app-logo2.png';

// Helper function to detect browser mode (normal vs incognito/private)
// Uses multiple detection methods with Safari-specific handling
const detectBrowserMode = async () => {
  try {
    // Method 1: sessionStorage test (simpler than localStorage, sometimes more reliable)
    try {
      const testKey = '__mode_detect_' + Date.now();
      const testValue = 'test_value_' + Math.random();

      // Try to write to sessionStorage
      sessionStorage.setItem(testKey, testValue);

      // Try to read back
      const retrieved = sessionStorage.getItem(testKey);

      // Clean up
      try {
        sessionStorage.removeItem(testKey);
      } catch (e) {
        // Ignore cleanup errors
      }

      if (retrieved === testValue) {
        return 'normal';
      }
    } catch (sessionStorageError) {
      // Fall through to next method
    }

    // Method 2: localStorage test (backup to sessionStorage)
    try {
      const testKey = '__mode_detect_' + Date.now();
      const testValue = 'test_value_' + Math.random();

      // Try to write to localStorage
      localStorage.setItem(testKey, testValue);

      // Try to read back
      const retrieved = localStorage.getItem(testKey);

      // Clean up
      try {
        localStorage.removeItem(testKey);
      } catch (e) {
        // Ignore cleanup errors
      }

      if (retrieved === testValue) {
        return 'normal';
      }
    } catch (localStorageError) {
      // Fall through to next method
    }

    // Method 3: IndexedDB test (alternative when storage APIs fail)
    if ('indexedDB' in window) {
      try {
        const dbRequest = window.indexedDB.open('__mode_detect_db__');

        return await new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve('normal');
          }, 500); // Quick timeout

          dbRequest.onsuccess = (event) => {
            clearTimeout(timeout);
            try {
              const db = event.target.result;
              db.close();
              window.indexedDB.deleteDatabase('__mode_detect_db__');
            } catch (err) {
              // Ignore cleanup errors
            }
            resolve('normal');
          };

          dbRequest.onerror = (event) => {
            clearTimeout(timeout);
            resolve('incognito');
          };

          dbRequest.onblocked = () => {
            clearTimeout(timeout);
            resolve('normal');
          };
        });
      } catch (indexedDBError) {
        // Fall through to fallback
      }
    }

    // Fallback: Assume normal mode for Safari and browsers where detection fails
    // It's safer to treat normal as normal than to incorrectly treat normal as private
    return 'normal';
  } catch (e) {
    return 'normal'; // Safe fallback
  }
};

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // TIER 3: Detect browser mode for session tracking
      const browserMode = await detectBrowserMode();

      const loginPayload = {
        password: password,
        browser_mode: browserMode
      };
      loginPayload.username = username;

      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginPayload),
      });

      // Check for network/connection issues
      if (!response) {
        throw new Error('Unable to connect to the system. Please check your internet connection and try again.');
      }

      let data;
      const contentType = response.headers.get('content-type');

      try {
        // Only try to parse JSON if response contains JSON
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          // Non-JSON response (likely proxy error, HTML, etc.)
          if (!response.ok) {
            throw new Error('Unable to connect to the system. Please try again in a moment or contact support if the problem persists.');
          }
          throw new Error('Invalid response format from server');
        }
      } catch (parseError) {
        // Handle JSON parsing errors
        if (parseError.message.includes('JSON')) {
          throw new Error('Unable to connect to the system. Please try again in a moment or contact support if the problem persists.');
        }
        throw parseError;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and user info in sessionStorage (clears on browser close)
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(data.user));

      // Track session creation timestamp for security auditing
      sessionStorage.setItem('session_created_at', new Date().toISOString());

      // TIER 3: Store session_id from backend if available
      if (data.session_id) {
        sessionStorage.setItem('session_id', data.session_id);
      }

      // Call the onLogin callback
      // NOTE: Do NOT call navigate() here - let App.js handle routing based on user state
      // This avoids race conditions where navigate happens before state updates
      onLogin(data.user, data.token);

      // Phase 1: Check if password reset is required
      // If user just verified email, they must set their own password before accessing the app
      if (data.user.password_reset_required === true || data.user.password_reset_required === 1) {
        // App.js will detect password_reset_required and route to password-reset-required page
        return;
      }

      // App.js will handle routing based on user role and state
      // For users without password_reset_required, App.js will navigate to appropriate dashboard
    } catch (err) {
      let errorMessage = err.message || 'An error occurred. Please try again.';

      // Handle specific network error types
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to the system. Please check your internet connection and try again.';
      } else if (err instanceof TypeError && err.message.includes('NetworkError')) {
        errorMessage = 'Unable to connect to the system. Please check your internet connection and try again.';
      } else if (err.name === 'AbortError') {
        errorMessage = 'The system took too long to respond. Please try again.';
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Side - Animated Gradient Background */}
      <div className="login-left-side">
        {/* Background Layers */}
        <div className="particles"></div>
        <div className="medical-pattern"></div>

        {/* Floating Medical Device Illustrations */}
        <div className="medical-float-1">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
            <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
            <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
            <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
            <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-2">
          <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
            <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
            <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
          </svg>
        </div>

        <div className="medical-float-3">
          <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="30" width="60" height="70" rx="8" fill="white" opacity="0.9"/>
            <rect x="30" y="45" width="40" height="25" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="35" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <circle cx="65" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="20" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
            <line x1="30" y1="55" x2="70" y2="55" stroke="white" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-4">
          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="55" cy="35" r="20" fill="white" opacity="0.9"/>
            <rect x="50" y="50" width="10" height="40" fill="white" opacity="0.9"/>
            <circle cx="55" cy="35" r="12" fill="rgba(102,126,234,0.3)"/>
            <path d="M 45,30 L 55,25 L 65,30" stroke="white" strokeWidth="2" fill="none"/>
            <rect x="35" y="85" width="40" height="15" rx="5" fill="white" opacity="0.8"/>
          </svg>
        </div>

        <div className="medical-float-5">
          <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="90" height="60" rx="5" fill="white" opacity="0.9"/>
            <rect x="20" y="20" width="80" height="50" rx="3" fill="rgba(102,126,234,0.3)"/>
            <path d="M 30,45 L 45,45 L 50,35 L 55,55 L 60,40 L 70,45 L 90,45" stroke="rgba(102,126,234,0.6)" strokeWidth="3" fill="none"/>
            <rect x="45" y="75" width="30" height="5" fill="white" opacity="0.8"/>
            <rect x="35" y="80" width="50" height="15" rx="3" fill="white" opacity="0.9"/>
          </svg>
        </div>

        <div className="medical-float-6">
          <svg width="100" height="110" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="25" width="50" height="60" rx="6" fill="white" opacity="0.9"/>
            <rect x="35" y="35" width="30" height="20" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="40" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <circle cx="60" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="85" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
          </svg>
        </div>

        {/* Left Side Content */}
        <div className="left-side-content">
          <div className="left-side-text">
            <h2>Welcome to</h2>
            <h1>
              <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
              {APP_NAME}
            </h1>
            <p>Manage your medical devices with ease and confidence</p>
          </div>
        </div>

      </div>

      {/* Right Side - Login Form */}
      <div className="login-right-side">
        <div className="login-box">
          <div className="login-header">
            <h3>Sign In</h3>
            <p>Enter your credentials to access your account</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="username">Username or Employee ID</label>
              <input
                type="text"
                id="username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                disabled={isLoading}
                placeholder="Enter your username or employee ID"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" className="action-btn action-primary action-btn-block btn-login" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="forgot-password-section">
            <a href="/forgot-password" className="forgot-password-link">
              Forgot Password?
            </a>
          </div>
        </div>
        <AuthPageFooter />
      </div>
    </div>
  );
}

export default Login;
